import { Fragment, useLayoutEffect, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  Dashboard,
  DashboardComponentNames,
  DragAndDropIllustration,
  FlowStatusEnum,
  getFlowFilters,
  getFlowProperties,
  IDashboardComponentProperties,
  ILayout,
  ILayoutComponent,
  IPerformanceReport,
  makeDynamicReportingPageStore,
  PageLayout,
  PageNames,
  PageViewModeEnum,
  useGetFiltersToolbarHeight,
  usePerformancePageStore,
  useTableSort,
  useWorkflows,
  useFilteredFlowsByDefaultQueryFilters,
} from '@diana/core';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { ReportingTable } from '../PerformanceReportPage/ReportingTable';
import { ReportingFilterRenderer } from '../PerformanceReportPage/Support';
import { PerformanceReportBuilderPageToolbar } from './PerformanceReportBuilderPageToolbar';
import { LayoutIconSelector } from './FormComponents/LayoutIconSelector';
import { LayoutNameField } from './FormComponents/LayoutNameField';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from './i18n';

export const PerformanceReportBuilderPageContent = (props: {
  layout: ILayout<IDashboardComponentProperties, DashboardComponentNames>;
}) => {
  const { layout } = props;

  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);

  const reportId = layout.id;
  const gridApiRef = useGridApiRef();
  const filtersToolbarHeight = useGetFiltersToolbarHeight(true);

  const reports = usePerformancePageStore((s) => s.reports);
  const setReports = usePerformancePageStore((s) => s.setReports);

  const useReportingPageStore = makeDynamicReportingPageStore(reportId);
  const setFlows = useReportingPageStore((s) => s.setFlows);
  const setAvailableFilters = useReportingPageStore((s) => s.setAvailableFilters);
  const viewMode = useReportingPageStore((s) => s.viewMode);

  const { isFetching: isContentLoading, data: flowsData } = useWorkflows({
    reportId,
    flowStatuses: [FlowStatusEnum.Active, FlowStatusEnum.Archived],
  });

  const flows = useFilteredFlowsByDefaultQueryFilters({
    flows: flowsData,
    defaultQueryFilters: layout?.defaultQueryFilters,
  });

  const titleFontSize = '18px';

  const isLayoutEmpty = useMemo(() => {
    function check(
      items: ILayoutComponent<IDashboardComponentProperties, DashboardComponentNames>[],
    ) {
      for (const item of items) {
        const containers = [DashboardComponentNames.Section, DashboardComponentNames.Container];
        if (!containers.includes(item.component)) return false;
        if (Array.isArray(item.components) && !check(item.components)) return false;
      }
      return true;
    }

    return check(layout.components);
  }, [layout.components]);

  const supportedProperties = useMemo(() => {
    return getFlowProperties({
      flow: flows ?? [],
      onlyEnabled: false,
      onlyDefaultFilter: false,
    });
  }, [flows]);

  const { sortModel, onSortModelChange } = useTableSort({ supportedProperties });

  useLayoutEffect(() => {
    if (!flows || !flows.length) return;
    setFlows(flows);
    setAvailableFilters(
      getFlowFilters({
        flow: flows,
        propertyToFilter: (property) => ({
          property,
          renderFilter: (filterProps) => (
            <ReportingFilterRenderer filterProps={filterProps} storeId={reportId} />
          ),
        }),
        onlyEnabled: false,
        onlyDefaultFilter: false,
      }),
    );
  }, [flows, setAvailableFilters, setFlows, reportId]);

  useLayoutEffect(() => {
    if (layout) {
      setReports(uniqBy([...reports, layout as IPerformanceReport], (r) => r.id));
      return () => setReports(reports.filter((r) => r.id !== layout.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  return (
    <PageLayout
      name={PageNames.PERFORMANCE_BUILDER}
      title={layout?.displayName}
      isContentLoading={isContentLoading}
    >
      <Stack width="100%" pt={1} spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1} minHeight={48}>
          <LayoutIconSelector />
          <LayoutNameField
            variant="standard"
            fontSize={titleFontSize}
            autoWidth
            sx={({ typography }) => ({
              '.MuiInput-root .MuiInput-input': { fontSize: titleFontSize },
              '.MuiInput-root:before': { borderColor: 'rgba(0,0,0,0)' },
              '.MuiInputBase-input, .MuiInputBase-input::placeholder': {
                minWidth: '492px',
                fontWeight: typography.fontWeightMedium,
              },
            })}
          />
        </Stack>

        {(!isContentLoading && !flows) || flows?.length === 0 ? undefined : (
          <Box display="flex" alignItems="center" minHeight={48}>
            <PerformanceReportBuilderPageToolbar
              reportId={reportId}
              layout={layout}
              gridApiRef={gridApiRef}
              flows={flows!}
              height={filtersToolbarHeight}
            />
          </Box>
        )}

        <Box>
          {!isContentLoading && layout && flows ? (
            viewMode === PageViewModeEnum.Board ? (
              isLayoutEmpty ? (
                <Stack
                  spacing={1}
                  sx={({ spacing }) => ({ minHeight: spacing(40) })}
                  alignItems="center"
                  justifyContent="center"
                >
                  <DragAndDropIllustration />
                  <Typography color="text.placeholder">{t('labels.noComponentsYet')}</Typography>
                </Stack>
              ) : (
                <Box sx={{ '.MuiGrid-item > div[id] > .MuiGrid-container': { mt: -2 } }}>
                  <Dashboard reportId={reportId} layout={layout} flows={flows} />
                </Box>
              )
            ) : (
              <Grid>
                <ReportingTable
                  ref={gridApiRef}
                  reportId={reportId}
                  flows={flows}
                  layout={layout}
                  forceEmptySelection={false}
                  disableLastVisited={true}
                  supportedProperties={supportedProperties}
                  sortModel={sortModel}
                  onSortModelChange={onSortModelChange}
                />
              </Grid>
            )
          ) : (
            <Fragment />
          )}
        </Box>
      </Stack>
    </PageLayout>
  );
};

export const PerformanceReportBuilderPageContentSkeleton = () => {
  return (
    <PageLayout name={PageNames.PERFORMANCE_BUILDER}>
      <Stack spacing={2} pt={1}>
        <Stack direction="row" width="100%" alignItems="center" minHeight={48} spacing={1}>
          <Skeleton variant="rounded" height={32} width={32} />
          <Skeleton variant="rounded" height={15} width={210} />
        </Stack>

        <Stack direction="row" width="100%" alignItems="center" minHeight={48}>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" height={32} width={150} />
            <Skeleton variant="rounded" height={32} width={100} />
          </Stack>
          <Box flex={1} />
          <Skeleton variant="rounded" height={32} width={100} />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Skeleton variant="rounded" height={250} width="25%" />
          <Skeleton variant="rounded" height={250} width="25%" />
        </Stack>
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={50} width="100%" />
          <Skeleton variant="rounded" height={20} width="100%" />
          <Skeleton variant="rounded" height={20} width="100%" />
        </Stack>
      </Stack>
    </PageLayout>
  );
};
